import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { DialogFooter } from "@/components/ui/dialog";
import { Loader2, Calendar, AlertCircle, CheckCircle2 } from "lucide-react";
import ScheduleSlotEditor from "@/components/menu/ScheduleSlotEditor";

export default function ProductAgendaTab({
  scheduleEnabled, setScheduleEnabled,
  scheduleSlots, setScheduleSlots,
  visibleWhenUnavailable, setVisibleWhenUnavailable,
  unavailableMessage, setUnavailableMessage,
  hideAfterExpiration, setHideAfterExpiration,
  loading, handleSubmit, onClose,
}) {
  return (
    <div className="space-y-4 mt-2 pr-2">
      <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200">
        <div>
          <p className="font-semibold text-indigo-800 text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Agenda de Disponibilidade
          </p>
          <p className="text-xs text-indigo-600 mt-0.5">Produto visível no menu mas bloqueado fora da janela configurada.</p>
        </div>
        <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} className="data-[state=checked]:bg-indigo-600" data-testid="schedule-enabled-toggle" />
      </div>

      {scheduleEnabled && (
        <>
          <div>
            <Label className="text-sm font-semibold text-gray-700 mb-2 block">Intervalos de disponibilidade</Label>
            <ScheduleSlotEditor slots={scheduleSlots} onChange={setScheduleSlots} />
            {scheduleSlots.length === 0 ? (
              <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                Adicione ao menos um intervalo para ativar o bloqueio por agenda.
              </div>
            ) : (
              <div className="mt-2 p-2.5 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-wide mb-1">Resumo da agenda</p>
                <div className="space-y-0.5">
                  {scheduleSlots.map((s, i) => {
                    const short = { monday:"Seg", tuesday:"Ter", wednesday:"Qua", thursday:"Qui", friday:"Sex", saturday:"Sáb", sunday:"Dom" };
                    const dayLabels = (s.days || []).map(d => short[d] ?? d).join(", ");
                    return (
                      <p key={i} className="text-xs text-indigo-800 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-indigo-400 shrink-0" />
                        {dayLabels}: {s.start_time}–{s.end_time}
                      </p>
                    );
                  })}
                </div>
                <p className="text-[10px] text-indigo-500 mt-1.5">Mensagem exibida será gerada automaticamente no idioma do restaurante.</p>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs text-gray-500">Mensagem personalizada <span className="text-gray-400">(sobrepõe geração automática)</span></Label>
            <Input value={unavailableMessage} onChange={e => setUnavailableMessage(e.target.value)} placeholder="Ex: Disponível somente na quarta-feira das 12h às 15h" className="mt-1 text-sm" data-testid="unavailable-message" />
            {unavailableMessage && (
              <p className="text-[10px] text-indigo-600 mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Mensagem personalizada será exibida a todos os idiomas.
              </p>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-3 space-y-3 border border-gray-200">
            <Label className="text-sm font-semibold text-gray-700">Comportamento fora da janela</Label>
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm text-gray-700">Visível quando indisponível</span>
                <p className="text-xs text-gray-400">Exibe o card bloqueado no menu. Se desativado, o produto fica oculto.</p>
              </div>
              <Switch checked={visibleWhenUnavailable} onCheckedChange={setVisibleWhenUnavailable} className="data-[state=checked]:bg-indigo-600 ml-3 shrink-0" data-testid="visible-when-unavailable" />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm text-gray-700">Ocultar após encerramento</span>
                <p className="text-xs text-gray-400">Oculta o produto depois que todos os horários do dia terminarem.</p>
              </div>
              <Switch checked={hideAfterExpiration} onCheckedChange={setHideAfterExpiration} className="data-[state=checked]:bg-indigo-600 ml-3 shrink-0" data-testid="hide-after-expiration" />
            </label>
          </div>
        </>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} disabled={loading || (scheduleEnabled && scheduleSlots.length === 0)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-schedule-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
        </Button>
      </DialogFooter>
    </div>
  );
}
